import { useState, useCallback, MutableRefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePrompt } from '../../../AppPrompt';
import { ROUTES } from 'common/constants/urls';
import { SaveOptions } from '../types/monitoring-report.types';

interface UseWizardNavigationProps {
  totalSteps: number;
  saveReport: (options?: SaveOptions) => Promise<void>;
  isDirty: boolean;
  hasUnsavedPhotoChanges: boolean;
  isSaving: boolean;
  refreshCompletionStatus?: () => void;
  deleteReport: (reportId: string) => Promise<string | void>;
  reportName: string;
  reportId: string;
  /** Wizard step indices that contain section editors (auto-save when navigating away). */
  sectionsStepIndices?: number[];
  /** Project id used for redirecting back to the project page after delete */
  projectId?: string;
  /** Optional guard called before any navigation (Next, Back, or step click). Return false to block. */
  navigationGuardRef?: MutableRefObject<(() => Promise<boolean>) | null>;
  /** Stable id per step (index-aligned), sent with saves so version history knows the step. */
  stepKeys?: string[];
  /** Called with the step being left when navigation doesn't save, so its open revision is closed. */
  onLeaveStep?: (stepKey: string) => void;
}

interface UseWizardNavigationReturn {
  activeStep: number;
  setActiveStep: (step: number | ((prev: number) => number)) => void;
  /** Returns true if the transition was applied, false if declined or no-op. */
  handleNext: () => Promise<boolean>;
  handleBack: () => Promise<boolean>;
  handleStepClick: (stepIndex: number) => Promise<boolean>;
  handleDelete: () => Promise<void>;
}

/**
 * Shared hook for wizard navigation logic
 * Handles step navigation, saving before navigation, and delete confirmation
 */
export const useWizardNavigation = ({
  totalSteps,
  saveReport,
  isDirty,
  hasUnsavedPhotoChanges,
  isSaving,
  refreshCompletionStatus,
  deleteReport,
  reportName,
  reportId,
  sectionsStepIndices,
  projectId,
  navigationGuardRef,
  stepKeys,
  onLeaveStep,
}: UseWizardNavigationProps): UseWizardNavigationReturn => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const { getConfirmation } = usePrompt();

  const shouldSaveBeforeNavigate = useCallback(
    (currentStep: number) => {
      const hasChanges = isDirty || hasUnsavedPhotoChanges;
      // Save on photo step (0), review budget step (1), and payapp/document section steps (if specified)
      const isPhotoOrBudgetStep = currentStep === 0 || currentStep === 1;
      const isSectionsStep = sectionsStepIndices?.includes(currentStep) ?? false;
      return hasChanges && (isPhotoOrBudgetStep || isSectionsStep);
    },
    [hasUnsavedPhotoChanges, isDirty, sectionsStepIndices],
  );

  // Leaving a step ends its revision in the version history: saved changes are tagged as a step
  // change (the backend closes the revision); with nothing to save, the step is closed directly.
  const saveOrLeaveStep = useCallback(
    async (currentStep: number) => {
      const stepKey = stepKeys?.[currentStep];
      if (shouldSaveBeforeNavigate(currentStep)) {
        await saveReport({ reason: 'step-change', step: stepKey });
      } else if (stepKey) {
        onLeaveStep?.(stepKey);
      }
    },
    [stepKeys, shouldSaveBeforeNavigate, saveReport, onLeaveStep],
  );

  const handleNext = useCallback(async (): Promise<boolean> => {
    if (navigationGuardRef?.current) {
      const canProceed = await navigationGuardRef.current();
      if (!canProceed) return false;
    }
    try {
      await saveOrLeaveStep(activeStep);
    } finally {
      setActiveStep(prev => Math.min(prev + 1, totalSteps - 1));
    }
    return true;
  }, [activeStep, navigationGuardRef, saveOrLeaveStep, totalSteps]);

  const handleBack = useCallback(async (): Promise<boolean> => {
    if (navigationGuardRef?.current) {
      const canProceed = await navigationGuardRef.current();
      if (!canProceed) return false;
    }
    // Check if we're on the photo management step and have unsaved changes
    if (activeStep === 0 && hasUnsavedPhotoChanges) {
      const isConfirmed = await getConfirmation({
        heading: 'Unsaved Changes',
        description: 'You have unsaved changes in the photo management step. Do you want to save your changes before going back?',
        confirmButton: { label: 'Go Back', color: 'primary' },
        cancelButton: { label: 'Continue Anyways', color: 'secondary' },
      });
      if (isConfirmed) return false;
      refreshCompletionStatus?.();
    }
    const stepKey = stepKeys?.[activeStep];
    if (stepKey) onLeaveStep?.(stepKey);
    setActiveStep(prev => Math.max(0, prev - 1));
    return true;
  }, [activeStep, navigationGuardRef, hasUnsavedPhotoChanges, getConfirmation, refreshCompletionStatus, stepKeys, onLeaveStep]);

  const handleStepClick = useCallback(
    async (stepIndex: number): Promise<boolean> => {
      if (stepIndex === activeStep) return false;
      if (isSaving) {
        toast.warning('Saving in progress. Please wait...');
        return false;
      }
      if (navigationGuardRef?.current) {
        const canProceed = await navigationGuardRef.current();
        if (!canProceed) return false;
      }
      try {
        await saveOrLeaveStep(activeStep);
      } finally {
        setActiveStep(stepIndex);
      }
      return true;
    },
    [activeStep, isSaving, navigationGuardRef, saveOrLeaveStep],
  );

  const handleDelete = useCallback(async () => {
    const isConfirmed = await getConfirmation({
      heading: 'Delete Report',
      description: `Are you sure you want to delete this report: ${reportName}?`,
      confirmButton: { label: 'Delete', color: 'error' },
      cancelButton: { label: 'Cancel', color: 'secondary' },
    });
    if (!isConfirmed) return;
    try {
      await deleteReport(reportId);
      toast.success('Report deleted successfully!');
      // After deletion, always return to the project page if projectId is known.
      // This avoids landing back on a stale wizard URL (e.g., step 1) due to history state.
      if (projectId) {
        navigate(`${ROUTES.PROJECTS}/${projectId}`);
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Error deleting report. Please try again.');
    }
  }, [getConfirmation, reportName, deleteReport, reportId, navigate]);

  return {
    activeStep,
    setActiveStep,
    handleNext,
    handleBack,
    handleStepClick,
    handleDelete,
  };
};
